import { Route, Routes } from "react-router-dom";

import { AppShell } from "@/components/AppShell";
import { RequireAuth } from "@/components/RequireAuth";
import { useBootstrap } from "@/hooks/useBootstrap";
import { Account } from "@/routes/Account";
import { ActivityLog } from "@/routes/ActivityLog";
import { FacilityDashboard } from "@/routes/FacilityDashboard";
import { ForgotPassword } from "@/routes/ForgotPassword";
import { GettingStarted } from "@/routes/GettingStarted";
import { InvitationAccept } from "@/routes/InvitationAccept";
import { InvitationsAdmin } from "@/routes/InvitationsAdmin";
import { Login } from "@/routes/Login";
import { NitrateNitriteOverview } from "@/routes/NitrateNitriteOverview";
import { NotFound } from "@/routes/NotFound";
import { OperatorHome } from "@/routes/OperatorHome";
import { PlatformDashboard } from "@/routes/PlatformDashboard";
import { PwsOnboarding } from "@/routes/PwsOnboarding";
import { ResetPassword } from "@/routes/ResetPassword";
import { RootRedirect } from "@/routes/RootRedirect";
import { RuleLibraryAdmin } from "@/routes/RuleLibraryAdmin";
import { SmtpSettingsAdmin } from "@/routes/SmtpSettingsAdmin";
import { SystemsList } from "@/routes/SystemsList";
import { TenantsAdmin } from "@/routes/TenantsAdmin";
import { UsersAdmin } from "@/routes/UsersAdmin";

function FullPageSpinner(): JSX.Element {
  return (
    <div className="min-h-full flex items-center justify-center">
      <div className="skeleton w-32 h-6" aria-label="Loading" />
    </div>
  );
}

export default function App(): JSX.Element {
  const { ready } = useBootstrap();
  if (!ready) return <FullPageSpinner />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/invitations/accept" element={<InvitationAccept />} />
      <Route path="/" element={<RootRedirect />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route
          path="/home"
          element={
            <RequireAuth requireScope="tenant">
              <OperatorHome />
            </RequireAuth>
          }
        />
        <Route path="/account" element={<Account />} />
        <Route path="/getting-started" element={<GettingStarted />} />
        <Route path="/systems" element={<SystemsList />} />
        <Route path="/systems/:pwsId" element={<FacilityDashboard />} />
        <Route
          path="/platform"
          element={
            <RequireAuth requireScope="platform">
              <PlatformDashboard />
            </RequireAuth>
          }
        />
        <Route
          path="/compliance/nitrate-nitrite"
          element={<NitrateNitriteOverview />}
        />
        <Route
          path="/admin/invitations"
          element={
            <RequireAuth requireRole="admin">
              <InvitationsAdmin />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequireAuth requireRole="admin">
              <UsersAdmin />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/smtp"
          element={
            <RequireAuth requireScope="platform">
              <SmtpSettingsAdmin />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/rules"
          element={
            <RequireAuth requireScope="platform">
              <RuleLibraryAdmin />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/onboard"
          element={
            <RequireAuth requireScope="platform">
              <PwsOnboarding />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/activity"
          element={
            <RequireAuth requireScope="platform">
              <ActivityLog />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/tenants"
          element={
            <RequireAuth requireScope="platform">
              <TenantsAdmin />
            </RequireAuth>
          }
        />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
